<script setup>
import { usePlaylistStore } from "@/stores/playlist.js";
import { enableScroll, disableScroll, scrollToSlide  } from "@/Helpers/scroll";
import { ref, onMounted, watch, getCurrentInstance } from 'vue';

const props = defineProps({
    row: {
        type: Object,
        default: undefined,
    },
    playlistId: {
        type: Number,
        default: undefined,
    },
    isIntersecting: Boolean,
    scormExport: Boolean,
})
const store = usePlaylistStore();
const instance = getCurrentInstance();
const isAnswered = ref(false);
const choices = ref(props.row.content.answers.map(answer => answer.content));
const componentID = 'a'+Math.random().toString(36).slice(2, 7);

const scormApi = ref(null);
if (props.scormExport) {
    scormApi.value = instance.appContext.config.globalProperties.$scorm.api;
}

watch(
    () => props.isIntersecting,
    (newVal, oldVal) => { 
        if (props.isIntersecting === true && isAnswered.value === false) {
            disableScroll();
        } else if (props.isIntersecting === true && isAnswered.value === true) {
            enableScroll();
        }
        if (newVal === false && oldVal === true) {
            enableScroll();
        }
    }
);

function setPreviousAnswer() {
    const answer = store.getPreviousAnswer(props.row.id);
    if (answer) {
        let answers = answer.split(',');
        answers.forEach((item) => {
            const elem = document.querySelector('input[value="'+item+'"]');
            if (elem) elem.click();
        });
        checkSurveyAnswer(null, true);
    }
}

onMounted(() => {
    setPreviousAnswer()
});

function checkSurveyAnswer(event, isPrev = false) {
    if (props.row.content.multiAnswer === 'true') {
        const answerItems = document.querySelectorAll(`#${componentID} input[name="multiAnswer-${componentID}"]:checked`);
        const allAnswers = document.querySelectorAll(`#${componentID} input[name="multiAnswer-${componentID}"]`);
        if (answerItems.length > 0) {
            allAnswers.forEach((answer) => {
                answer.setAttribute('disabled', 'disabled');
            });
            const selectedAnswers = Array.from(answerItems).map(item => item.value);
            
            if (!isPrev) saveResponse(props.row.content.question, selectedAnswers, true);
            enableScroll();
            isAnswered.value = true;
            if (!isPrev && props.row.content.autoSkip === 'true') {
                scrollToSlide(store.currentSlideIndex + 1);
            }
        } else {
            alert('You must select one of the answers');
        }
    } else {
        let answerItem = document.querySelector(`#${componentID} input[name="singleAnswer-${componentID}"]:checked`);
        const allAnswers = document.querySelectorAll(`#${componentID} input[name="singleAnswer-${componentID}"]`);
        if (answerItem) {
            allAnswers.forEach((answer) => {
                answer.setAttribute('disabled', 'disabled');
            });
           
            if (!isPrev) saveResponse(props.row.content.question, answerItem.value, true);
            enableScroll();
            isAnswered.value = true;
            if (!isPrev && props.row.content.autoSkip === 'true') {
                scrollToSlide(store.currentSlideIndex + 1);
            }
        } else {
            alert('You must select one of the answers');
        }
    }
}

function saveResponse(strQuestion, strAnswer, isCorrect) {
    if (typeof strAnswer === 'object') strAnswer = strAnswer.toString();
    
    const data = {
        playlist_id: props.playlistId,
        playlist_card_row_id: props.row.id,
        type: props.row.type,
        question: strQuestion,
        answer: strAnswer,
        isCorrect: isCorrect,
    }

    if (props.scormExport && scormApi.value) {
        data.choices = choices.value;
        data.correct_responses = props.row.content.answers.map((answer, index) => index);
        scormApi.value.recordInteraction(data);
    } else {
        const url = '/activity/store_response'
        axios.post(url, data)
            .then(function (response) {
                // do nothing
            })
            .catch(function (error) {
                // do nothing
            });
    }
}
</script>
<template>
    <div :id="componentID" class="relative p-fl-4 w-full">
        <div class="font-black text-fl-lg w-full pb-fl-2" v-text="row.content.question" />
        <ul class="flex flex-col gap-y-1.5 pb-fl-2 my-2">
            <li
                v-for="(answer, index) in row.content.answers"
                :key="`checkbox-${index}`"
                class="flex flex-row gap-1.5 items-center w-full p-2 rounded-lg border"
            >
                <template v-if="row.content.multiAnswer === 'true'">
                    <input :id="`checkbox-${componentID}-${index}`" type="checkbox" :value="answer.content" :name="`multiAnswer-${componentID}`">
                    <label class="grow" :for="`checkbox-${componentID}-${index}`">{{ answer.content }}</label>
                </template>
                <template v-else>
                    <input :id="`radio-${componentID}-${index}`" type="radio" :value="answer.content" :name="`singleAnswer-${componentID}`">
                    <label class="grow" :for="`radio-${componentID}-${index}`">{{ answer.content }}</label>
                </template>                
            </li>
        </ul>
        <div class="flex flex-col w-full">
            <button
                v-if="!isAnswered"
                type="button"
                class="place-self-end rounded-full 
                px-fl-4 
                py-fl-2 
                font-bold 
                shadow-sm 
                border-2
                focus-visible:outline 
                focus-visible:outline-2 
                focus-visible:outline-offset-2 
                focus-visible:outline-white"
                @click="checkSurveyAnswer"
            >
                Submit
            </button>
            <p v-else>
                {{ row.content.responseMessage }}
            </p>
        </div>
    </div>
</template>

<style scoped>
button, li {
    border-color: var(--button_bg);
    background-color: var(--button_bg);
    color: var(--button_text);
}

button:hover, li:hover {
    background-color: var(--button_hover_bg);
    color: var(--button_hover_text);
}
</style>